/**
 * Warrior hero behavior, shared by sections/warrior-hero.liquid and
 * sections/warrior-hero-lower.liquid: media carousel + thumbnails per section,
 * the window.WayfxWarriorUi helper namespace (lightbox no-op, infobar
 * fallback) and the infobar scroll fallback itself.
 *
 * Boots once per `[data-rtnu-warrior-hero]` root. The root passes:
 *   data-section-id  Shopify section id (resolves the #shopify-section-* wrapper)
 *
 * The Whole Package / Fire Starter upsell forms in the lower section are plain
 * product forms with a hidden selling_plan input; nothing here touches them.
 * The buy box lives in assets/rtnu-buybox.js.
 */
(() => {
  if (window.__rtnuWarriorHeroBound) return;
  window.__rtnuWarriorHeroBound = true;

  const MAIN_SELECTOR = '.wayfx-product__photos--featured-warrior.embla';
  const THUMBS_SELECTOR = '.wayfx-product__photos--thumbnails-warrior.embla';
  const THUMB_SELECTOR = '.wayfx-product__photos--thumbnail';

  const ensureWarriorUiHelpers = () => {
    if (window.WayfxWarriorUi) return window.WayfxWarriorUi;

    const bindLightbox = () => {};

    const initInfobarFallback = () => {
      if (window.__wayfxInfobarFallbackBound) return;

      const infoBar = document.querySelector('.wayfx-product__infobar');
      const hero = document.querySelector('.wayfx-product__hero');
      const productRoot = document.querySelector('.wayfx-product');
      if (!infoBar || !hero || !productRoot) return;

      let ticking = false;
      const update = () => {
        const heroHeight = hero.offsetHeight;
        const productTop = productRoot.getBoundingClientRect().top + window.scrollY;
        const productBottom = productTop + productRoot.offsetHeight;
        const reachedBottom = window.scrollY >= productBottom - window.innerHeight;

        infoBar.classList.toggle('is-visible', window.scrollY > heroHeight && !reachedBottom);
        ticking = false;
      };

      const onScroll = () => {
        if (ticking) return;
        window.requestAnimationFrame(update);
        ticking = true;
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
      window.__wayfxInfobarFallbackBound = true;
    };

    window.WayfxWarriorUi = { bindLightbox, initInfobarFallback };
    window.dispatchEvent(new CustomEvent('wayfx:warrior-ui-ready'));

    return window.WayfxWarriorUi;
  };

  const initCarousel = (sectionRoot) => {
    const mainCarouselNode = sectionRoot.querySelector(MAIN_SELECTOR);
    const thumbnailsNode = sectionRoot.querySelector(THUMBS_SELECTOR);
    if (!mainCarouselNode || typeof EmblaCarousel === 'undefined') return;

    if (typeof mainCarouselNode._emblaCleanup === 'function') {
      mainCarouselNode._emblaCleanup();
    }

    const mainViewport = mainCarouselNode.querySelector('.embla__viewport');
    if (!mainViewport) return;

    const mainOptions = {
      align: 'start',
      containScroll: 'trimSnaps',
      slidesToScroll: 1,
      watchDrag: window.innerWidth <= 768,
    };

    const mainEmbla = EmblaCarousel(mainViewport, mainOptions);
    mainCarouselNode._emblaApi = mainEmbla;

    let thumbnailsEmbla = null;
    const thumbnailListeners = [];

    const cleanupThumbnailListeners = () => {
      thumbnailListeners.forEach(({ node, onClick, onKeydown }) => {
        node.removeEventListener('click', onClick);
        node.removeEventListener('keydown', onKeydown);
      });
      thumbnailListeners.length = 0;
    };

    const initThumbnailsCarousel = () => {
      if (!thumbnailsNode || thumbnailsEmbla) return;
      const thumbnailsViewport = thumbnailsNode.querySelector('.embla__viewport');
      if (!thumbnailsViewport) return;

      thumbnailsEmbla = EmblaCarousel(thumbnailsViewport, {
        align: 'start',
        containScroll: 'trimSnaps',
        dragFree: true,
        slidesToScroll: 1,
      });
    };

    const updateThumbnails = () => {
      if (!thumbnailsNode) return;

      const selectedIndex = mainEmbla.selectedScrollSnap();
      thumbnailsNode.querySelectorAll(THUMB_SELECTOR).forEach((slide, index) => {
        const isSelected = index === selectedIndex;
        slide.classList.toggle('is-selected', isSelected);
        slide.setAttribute('aria-current', isSelected ? 'true' : 'false');
      });

      thumbnailsEmbla?.scrollTo(selectedIndex);
    };

    const bindThumbnailEvents = () => {
      if (!thumbnailsNode) return;

      thumbnailsNode.querySelectorAll(THUMB_SELECTOR).forEach((slide, index) => {
        const onClick = () => mainEmbla.scrollTo(index);
        const onKeydown = (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            mainEmbla.scrollTo(index);
          }
        };

        slide.addEventListener('click', onClick);
        slide.addEventListener('keydown', onKeydown);
        thumbnailListeners.push({ node: slide, onClick, onKeydown });
      });
    };

    const onSelect = () => updateThumbnails();

    mainEmbla.on('select', onSelect);
    mainEmbla.on('reInit', onSelect);

    initThumbnailsCarousel();
    bindThumbnailEvents();
    onSelect();

    let resizeTimer = null;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        mainEmbla.reInit({ ...mainOptions, watchDrag: window.innerWidth <= 768 });
        thumbnailsEmbla?.reInit();
        onSelect();
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    mainCarouselNode._emblaCleanup = () => {
      window.removeEventListener('resize', handleResize);
      cleanupThumbnailListeners();
      if (thumbnailsEmbla) {
        thumbnailsEmbla.destroy();
        thumbnailsEmbla = null;
      }
      mainEmbla.destroy();
      mainCarouselNode._emblaApi = null;
      mainCarouselNode._emblaCleanup = null;
    };
  };

  const initHero = (root) => {
    if (root.dataset.rtnuWarriorHeroReady) return;
    root.dataset.rtnuWarriorHeroReady = 'true';

    const sectionRoot = document.getElementById(`shopify-section-${root.dataset.sectionId}`) || root;
    initCarousel(sectionRoot);
    ensureWarriorUiHelpers().initInfobarFallback();
  };

  const initAll = () => document.querySelectorAll('[data-rtnu-warrior-hero]').forEach(initHero);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
